import $ from 'jquery';
import { v4 as uuid } from 'uuid';

$(() => {
    const section = document.querySelector('[data-checkout-cross-sell]');
    const form = section?.closest('form.Checkout');
    if (!section || !form) return;
    const cards = new Map();
    let busy = false;
    let nonce = '';
    let deferredReview = false;
    // Do not let a parallel totals request save a cart snapshot from before the quick-add.
    $.ajaxPrefilter((settings, _original, xhr) => {
        if (busy && settings.url?.includes('wc-ajax=update_order_review')) {
            deferredReview = true;
            xhr.abort();
        }
    });
    const matches = (variant, values) => Object.entries(values).every(([name, value]) => !value || !variant.attributes[name] || variant.attributes[name] === value);
    const plain = (html) => { const element = document.createElement('span'); element.innerHTML = html; return element.textContent.trim(); };
    const fallback = (image) => {
        const src = image.dataset.crossSellImageFallback;
        if (!src) return;
        delete image.dataset.crossSellImageFallback;
        image.removeAttribute('srcset');
        image.src = src;
    };
    section.addEventListener('error', (event) => { if (event.target.matches('img')) fallback(event.target); }, true);
    section.querySelectorAll('img').forEach((image) => { if (image.complete && !image.naturalWidth) fallback(image); });
    section.querySelectorAll('[data-checkout-accessory]').forEach((card) => {
        const options = JSON.parse(card.dataset.options);
        cards.set(card, {
            variants: options.variants, options: options.attributes, price: options.price, attributes: JSON.parse(card.dataset.attributes),
            request: uuid(), key: '', inCart: false, retry: false, pending: null, error: '', imageKey: '',
            baseImage: card.querySelector('[data-accessory-image]').innerHTML,
        });
    });
    const confirmation = (card) => section.querySelector(`[data-accessory-confirmation="${card.dataset.checkoutAccessory}"]`);
    const collapse = (card, focus = false) => {
        const current = card.querySelector('[data-accessory-selector][aria-expanded="true"]');
        card.querySelectorAll('[data-accessory-options]').forEach((list) => { list.hidden = true; });
        if (current) { current.setAttribute('aria-expanded', 'false'); if (focus) current.focus({ preventScroll: true }); }
    };
    const render = () => {
        const waiting = form.classList.contains('is-updating') || form.classList.contains('processing');
        const uncertain = [...cards.values()].some((state) => state.retry);
        section.classList.toggle('is-uncertain', uncertain);
        form.classList.toggle('is-cross-selling', busy);
        section.setAttribute('aria-busy', String(busy));
        const pay = form.querySelector('#place_order');
        if (busy || uncertain || waiting || form.classList.contains('is-stale')) pay?.setAttribute('aria-disabled', 'true');
        else pay?.removeAttribute('aria-disabled');
        let cheapest = null;
        cards.forEach((state, card) => {
            const locked = busy || state.retry || !!state.key || state.inCart || waiting;
            const before = {};
            // Earlier attributes constrain later ones; changing a color cannot trap a selected size.
            state.options.forEach((attribute) => {
                const available = attribute.options.filter((option) => state.variants.some((variant) => variant.available && matches(variant, { ...before, [attribute.name]: option.value })));
                if (!available.some((option) => option.value === state.attributes[attribute.name])) state.attributes[attribute.name] = '';
                if (attribute.options.length === 1 && available.length === 1) state.attributes[attribute.name] = available[0].value;
                const selected = attribute.options.find((option) => option.value === state.attributes[attribute.name]);
                const trigger = [...card.querySelectorAll('[data-accessory-selector]')].find((button) => button.dataset.accessorySelector === attribute.name);
                if (trigger) {
                    trigger.querySelector('[data-accessory-value]').textContent = selected?.label || 'Wybierz';
                    trigger.disabled = locked || trigger.dataset.fixed === 'true';
                }
                const list = [...card.querySelectorAll('[data-accessory-options]')].find((element) => element.dataset.accessoryOptions === attribute.name);
                list?.querySelectorAll('[data-accessory-option]').forEach((button) => {
                    const chosen = button.dataset.accessoryOption === selected?.value;
                    button.disabled = locked || !available.some((option) => option.value === button.dataset.accessoryOption);
                    button.setAttribute('aria-selected', String(chosen));
                    button.querySelector('img').hidden = !chosen;
                    button.tabIndex = chosen && !button.disabled ? 0 : -1;
                });
                before[attribute.name] = state.attributes[attribute.name];
            });
            const complete = state.options.every((attribute) => state.attributes[attribute.name]);
            state.selection = complete ? state.variants.find((variant) => variant.available && matches(variant, state.attributes)) : null;
            const selection = state.retry ? state.pending : state.selection;
            const imageKey = selection ? String(selection.id) : 'base';
            if (imageKey !== state.imageKey) {
                card.querySelector('[data-accessory-image]').innerHTML = selection ? selection.image : state.baseImage;
                state.imageKey = imageKey;
            }
            card.querySelector('[data-accessory-price]').innerHTML = selection ? selection.price : state.price;
            if (selection) {
                const url = new URL(selection.url);
                Object.entries(state.attributes).forEach(([name, value]) => url.searchParams.set(name, value));
                card.querySelectorAll('a').forEach((link) => { link.href = url.href; });
                if (!cheapest || selection.amount < cheapest.amount) cheapest = selection;
            } else {
                state.variants.filter((variant) => variant.available).forEach((variant) => { if (!cheapest || variant.amount < cheapest.amount) cheapest = variant; });
            }
            card.classList.toggle('is-added', !!state.key || state.inCart);
            const add = card.querySelector('[data-accessory-add]');
            add.disabled = busy || waiting || !!state.key || state.inCart || (!state.retry && !state.selection) || (uncertain && !state.retry);
            const label = state.key ? '✓ Dodano' : state.inCart ? 'W koszyku' : state.retry ? 'Ponów' : '+ Dodaj';
            add.querySelector('[data-accessory-add-label]').textContent = label;
            add.setAttribute('aria-label', `${label}: ${card.querySelector('[data-accessory-name]').textContent}`);
            const error = card.querySelector('[data-accessory-error]');
            const missing = state.options.filter((attribute) => !state.attributes[attribute.name]).map((attribute) => attribute.label.toLocaleLowerCase('pl'));
            error.textContent = state.error || (missing.length ? `Wybierz: ${missing.join(' i ')}.` : '');
            error.hidden = !error.textContent;
            const notice = confirmation(card);
            notice.hidden = !state.key;
            if (state.key) {
                notice.querySelector('[data-accessory-confirmation-text]').textContent = `Dodano ${card.querySelector('[data-accessory-name]').textContent} · +${plain(selection.amount_html)}`;
                const undo = notice.querySelector('[data-accessory-undo]');
                undo.disabled = busy || waiting || (uncertain && !state.retry);
                undo.textContent = state.retry ? 'Ponów cofnięcie' : 'Cofnij';
            }
            if (locked) collapse(card);
        });
        if (cheapest) section.querySelector('[data-checkout-cross-sell-from]').innerHTML = cheapest.amount_html;
        section.querySelector('[data-accessory-confirmations]').hidden = ![...cards.values()].some((state) => state.key);
    };
    const readState = () => {
        const snapshot = form.querySelector('.CheckoutCrossSellState');
        if (!snapshot) return;
        nonce = snapshot.dataset.nonce;
        const keys = JSON.parse(snapshot.dataset.keys);
        const products = JSON.parse(snapshot.dataset.products);
        cards.forEach((state, card) => {
            if (state.key && !keys.includes(state.key) && !state.retry) { state.key = ''; state.request = uuid(); }
            state.inCart = !state.key && !state.retry && products.includes(Number(card.dataset.checkoutAccessory));
        });
    };
    const getNonce = async () => {
        if (nonce) return nonce;
        const snapshot = await $.ajax({ url: jsData.ajaxUrl, dataType: 'json', cache: false, timeout: 20000, data: { action: 'verenza_get_cart_items' } });
        if (!snapshot.nonce) throw new Error('Nie udało się odczytać koszyka. Spróbuj ponownie.');
        nonce = snapshot.nonce;
        return nonce;
    };
    const mutate = async (card, undo = false) => {
        const state = cards.get(card);
        if (busy || form.classList.contains('is-updating') || form.classList.contains('processing') || (!undo && !state.retry && (!state.selection || state.key || state.inCart))) return;
        if (state.retry) undo = state.retryUndo;
        if (!state.retry) state.pending = { ...state.selection, attributes: { ...state.attributes } };
        state.error = '';
        busy = true;
        render();
        card.querySelector('[data-accessory-add-label]').textContent = undo ? 'Cofamy…' : 'Dodajemy…';
        let success = false;
        try {
            const response = await $.ajax({
                url: jsData.ajaxUrl, type: 'POST', dataType: 'json', timeout: 20000,
                data: { action: 'ravessa_sidebar_cart_action', operation: undo ? 'cross_sell_undo' : 'cross_sell_add', nonce: await getNonce(), request_id: state.request,
                    product_id: card.dataset.checkoutAccessory, variation_id: state.pending.id || 0, attributes: state.pending.attributes, quantity: 1 },
            });
            if (!response.success || typeof response.data?.cart_html !== 'string' || typeof response.data?.cross_sell_key !== 'string') throw new Error(response.data?.message || 'Nie udało się potwierdzić zmiany. Ponów operację.');
            state.key = undo ? '' : response.data.cross_sell_key;
            if (undo) state.request = uuid();
            state.retry = false;
            state.inCart = false;
            state.pending = null;
            nonce = response.data.nonce || nonce;
            section.querySelector('[data-accessory-live]').textContent = undo ? 'Cofnięto dodanie produktu.' : `Dodano ${card.querySelector('[data-accessory-name]').textContent}. Aktualizujemy zamówienie.`;
            $(document.body).trigger('ravessa:cart-updated', [response.data]);
            $(document.body).trigger('wc_fragment_refresh');
            success = true;
        } catch (error) {
            const data = error.responseJSON?.data;
            if (data?.refresh) nonce = '';
            state.retry = !data;
            state.retryUndo = undo;
            state.error = data?.message || 'Nie udało się potwierdzić zmiany koszyka. Ponów operację przed złożeniem zamówienia.';
            if (data && !undo) { state.pending = null; state.request = uuid(); }
            if (data?.cart) { $(document.body).trigger('ravessa:cart-updated', [data.cart]); deferredReview = true; }
        } finally {
            busy = false;
            if (success || deferredReview) { deferredReview = false; $(document.body).trigger('update_checkout'); }
            render();
            if (success) (undo ? card.querySelector('[data-accessory-add]') : confirmation(card).querySelector('button')).focus({ preventScroll: true });
        }
    };
    section.addEventListener('click', (event) => {
        const add = event.target.closest('[data-accessory-add]');
        if (add) { mutate(add.closest('[data-checkout-accessory]')); return; }
        const undo = event.target.closest('[data-accessory-undo]');
        if (undo) { mutate([...cards.keys()].find((card) => card.dataset.checkoutAccessory === undo.dataset.accessoryUndo), true); return; }
        const trigger = event.target.closest('[data-accessory-selector]');
        if (trigger && !trigger.disabled) {
            const card = trigger.closest('[data-checkout-accessory]');
            const open = trigger.getAttribute('aria-expanded') !== 'true';
            cards.forEach((_state, item) => collapse(item));
            if (open) {
                const list = document.getElementById(trigger.getAttribute('aria-controls'));
                list.hidden = false;
                trigger.setAttribute('aria-expanded', 'true');
                (list.querySelector('[aria-selected="true"]:not(:disabled)') || list.querySelector('button:not(:disabled)'))?.focus({ preventScroll: true });
            }
        }
        const option = event.target.closest('[data-accessory-option]');
        if (option && !option.disabled) {
            const card = option.closest('[data-checkout-accessory]');
            const state = cards.get(card);
            state.attributes[option.closest('[data-accessory-options]').dataset.accessoryOptions] = option.dataset.accessoryOption;
            state.error = '';
            render();
            collapse(card, true);
        }
    });
    section.addEventListener('keydown', (event) => {
        const list = event.target.closest('[data-accessory-options]');
        if (event.key === 'Escape') { event.preventDefault(); const card = event.target.closest('[data-checkout-accessory]'); if (card) collapse(card, true); return; }
        if (!list || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const options = [...list.querySelectorAll('button:not(:disabled)')];
        const index = options.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options[next]?.focus({ preventScroll: true });
    });
    document.addEventListener('click', (event) => { cards.forEach((_state, card) => { if (!card.contains(event.target)) collapse(card); }); });
    $(document.body).on('update_checkout.ravessaCrossSell', render).on('updated_checkout.ravessaCrossSell', () => { readState(); render(); });
    readState();
    render();
    section.classList.add('is-ready');
});
